/**
 * WHERE the SPA talks to the edge. One module owns both answers, because getting
 * either wrong is invisible in dev and fatal in production.
 *
 * ## HTTP — everything is under `/api`
 *
 * The production ingress routes `Host(app.4fantasia.com) && PathPrefix(/api)` to
 * the API service and strips the prefix before the request reaches the Hono app
 * (whose own route mounts stay at root, unchanged). Anything the browser requests
 * at a ROOT path (`/auth/login`, `/me`, `/companions`, …) falls through the
 * catch-all to the nginx SPA container, which answers `index.html` with a **200**
 * — an HTML body where JSON was expected, not even a clean network error. So an
 * unprefixed client cannot reach the API at all, no matter how healthy the pod is.
 *
 * `/api` is also the only split that works for the admin console: the admin SPA is
 * served at `/admin/` and the staff API group is mounted at `/admin` — same origin,
 * same path. `/api/admin/…` → stripPrefix → `/admin/…` disambiguates them; a
 * root-path router at the ingress cannot.
 *
 * The value is build-time configurable (`VITE_API_BASE_URL`) rather than a literal
 * at the construction site, so dev and prod can differ without the two silently
 * drifting apart again — and so this contract has exactly ONE home.
 *
 * ## WebSocket — `/ws`, deliberately NOT prefixed
 *
 * The `/ws` route is mounted at root and the ingress routes it BARE, without the
 * stripPrefix middleware. Prefixing it would 404 the upgrade and silently kill
 * proactive delivery, cross-device sync, and the media-completion relay. It is a
 * separate constant here (not `${base}/ws`) precisely so a future base-url change
 * cannot drag the socket along with it.
 */

/**
 * The prefix every HTTP request is issued under. `/api` in production; override
 * at build time with `VITE_API_BASE_URL` (e.g. an absolute origin when the SPA is
 * served from somewhere other than the API host).
 */
export const DEFAULT_API_BASE_URL = '/api';

/**
 * The realtime endpoint. Root-mounted and un-prefixed at the ingress — see the
 * module doc. Never derive this from {@link apiBaseUrl}.
 */
export const WS_PATH = '/ws';

/** Read the configured API prefix, falling back to {@link DEFAULT_API_BASE_URL}. */
export function apiBaseUrl(env: { readonly VITE_API_BASE_URL?: string } = import.meta.env): string {
  const configured = env.VITE_API_BASE_URL?.trim();
  return configured ? configured.replace(/\/+$/, '') : DEFAULT_API_BASE_URL;
}
